import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/Dashboard.vue'),
      },
      {
        path: 'organizations',
        name: 'organizations',
        component: () => import('@/pages/Organizations.vue'),
      },
      {
        path: 'organizations/:slug',
        name: 'organization-detail',
        component: () => import('@/pages/OrganizationDetail.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/points',
        name: 'organization-points',
        component: () => import('@/pages/OrgPoints.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/trails',
        name: 'organization-trails',
        component: () => import('@/pages/OrgTrails.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/viewpoints',
        name: 'organization-viewpoints',
        component: () => import('@/pages/OrgViewpoints.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/hunts',
        name: 'organization-hunts',
        component: () => import('@/pages/OrgHunts.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/campaigns',
        name: 'organization-campaigns',
        component: () => import('@/pages/OrgCampaigns.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/models',
        name: 'organization-models',
        component: () => import('@/pages/Org3DModels.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/analytics',
        name: 'organization-analytics',
        component: () => import('@/pages/OrgAnalytics.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/members',
        name: 'organization-members',
        component: () => import('@/pages/OrgMembers.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/tags',
        name: 'organization-tags',
        component: () => import('@/pages/OrgTags.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/badges',
        name: 'organization-badges',
        component: () => import('@/pages/OrgBadges.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/access-codes',
        name: 'organization-access-codes',
        component: () => import('@/pages/OrgAccessCodes.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/sponsors',
        name: 'organization-sponsors',
        component: () => import('@/pages/OrgSponsors.vue'),
        props: true,
      },
      {
        path: 'organizations/:slug/subscription',
        name: 'organization-subscription',
        component: () => import('@/pages/OrgSubscription.vue'),
        props: true,
      },
      {
        path: 'trails/:slug',
        name: 'trail-detail',
        component: () => import('@/pages/TrailDetail.vue'),
        props: true,
      },
      {
        path: 'hunts/:slug',
        name: 'hunt-detail',
        component: () => import('@/pages/HuntDetail.vue'),
        props: true,
      },
      {
        path: 'campaigns/:slug',
        name: 'campaign-detail',
        component: () => import('@/pages/CampaignDetail.vue'),
        props: true,
      },
      {
        path: 'points/:id',
        name: 'point-detail',
        component: () => import('@/pages/PointDetail.vue'),
        props: true,
      },
      {
        path: 'viewpoints/:id',
        name: 'viewpoint-detail',
        component: () => import('@/pages/ViewpointDetail.vue'),
        props: true,
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/pages/AdminUsers.vue'),
        meta: { admin: true },
      },
      {
        path: 'admin/stats',
        name: 'admin-stats',
        component: () => import('@/pages/AdminStats.vue'),
        meta: { admin: true },
      },
      {
        path: 'admin/analytics',
        name: 'admin-analytics',
        component: () => import('@/pages/AdminAnalytics.vue'),
        meta: { admin: true },
      },
      {
        path: 'admin/badges',
        name: 'admin-badges',
        component: () => import('@/pages/AdminBadges.vue'),
        meta: { admin: true },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/pages/Profile.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFound.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.initialize()
  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  // Admin-only routes: bounce non-admins back to the dashboard.
  if (to.meta.admin && !auth.isAdmin) {
    return { name: 'dashboard' }
  }
})
